import { useState } from 'react';
import { TableRow, Typography, IconButton, Box, useTheme, CircularProgress, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

import { RowTableCell, ConfirmationDialog } from '../../../common/components';
import { useToggleReconciliation, useDeleteBankMovement } from '../../../hooks/useBankMovements';
import { useAuth } from '../../../hooks/useAuth';
import { formatCurrency, formatDate } from '../utils/formatters';

const MovementRow = ({ movement, movementTypes = [], legalEntities = [], onEdit }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isReconcileDialogOpen, setIsReconcileDialogOpen] = useState(false);

  const toggleReconciliationMutation = useToggleReconciliation();
  const deleteMovementMutation = useDeleteBankMovement();

  const amountColor =
    Number(movement.amount ?? 0) >= 0 ? theme.palette.success.main : theme.palette.error.main;
  const bookBalanceColor =
    Number(movement.accumulated_balance ?? 0) >= 0
      ? theme.palette.success.main
      : theme.palette.error.main;

  const isAdmin = user?.role?.name === 'Administrador';
  const canUnreconcile = isAdmin;
  const canDelete = !movement.is_reconciled;

  const handleReconcileClick = () => {
    if (movement.is_reconciled && !canUnreconcile) {
      return;
    }
    setIsReconcileDialogOpen(true);
  };

  const handleCancelReconcile = () => {
    setIsReconcileDialogOpen(false);
  };

  const handleConfirmReconcile = async () => {
    try {
      await toggleReconciliationMutation.mutateAsync(movement.uuid);
      setIsReconcileDialogOpen(false);
    } catch (error) {
      console.error('Error toggling reconciliation:', error);
    }
  };

  const handleDeleteClick = () => {
    if (!canDelete) {
      return;
    }
    setIsDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteMovementMutation.mutateAsync(movement.uuid);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting movement:', error);
    }
  };

  const handleEditClick = () => {
    if (movement.is_reconciled) {
      return;
    }
    onEdit(movement);
  };

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: movement.is_reconciled
            ? 'rgba(76, 175, 80, 0.08)'
            : 'rgba(255, 255, 255, 1)',
          borderLeft: movement.is_reconciled
            ? `4px solid ${theme.palette.success.main}`
            : `4px solid ${theme.palette.error.main}`,
        }}
      >
        <RowTableCell>{formatDate(movement.emission_date)}</RowTableCell>

        <RowTableCell>{movement.due_date ? formatDate(movement.due_date) : '-'}</RowTableCell>

        <RowTableCell>{movement.movement_type_name}</RowTableCell>

        <RowTableCell>{movement.concept || '-'}</RowTableCell>

        <RowTableCell>{movement.payment_order_number ?? '-'}</RowTableCell>

        <RowTableCell>{movement.voucher_number || '-'}</RowTableCell>

        <RowTableCell>{movement.legal_entity_name || '-'}</RowTableCell>

        <RowTableCell sx={{ textAlign: 'right' }}>
          <Typography color={amountColor} fontFamily="monospace" fontWeight="medium">
            {formatCurrency(movement.amount, 2)}
          </Typography>
        </RowTableCell>

        <RowTableCell sx={{ textAlign: 'right' }}>
          <Typography color={bookBalanceColor} fontFamily="monospace" fontWeight="medium">
            {formatCurrency(movement.accumulated_balance, 2)}
          </Typography>
        </RowTableCell>

        <RowTableCell sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <Tooltip
              title={
                movement.is_reconciled && !canUnreconcile
                  ? 'Solo gerentes pueden desconciliar'
                  : movement.is_reconciled
                  ? 'Marcar como no conciliado'
                  : 'Marcar como conciliado'
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleReconcileClick}
                  disabled={
                    toggleReconciliationMutation.isPending ||
                    (movement.is_reconciled && !canUnreconcile)
                  }
                  sx={{
                    color: movement.is_reconciled
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                  }}
                >
                  {toggleReconciliationMutation.isPending ? (
                    <CircularProgress size={20} />
                  ) : movement.is_reconciled ? (
                    <CheckCircleIcon />
                  ) : (
                    <CancelIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <IconButton
              size="small"
              onClick={handleEditClick}
              disabled={movement.is_reconciled}
              color="primary"
              title={
                movement.is_reconciled
                  ? 'No se puede editar un movimiento conciliado'
                  : 'Editar movimiento'
              }
            >
              <EditIcon />
            </IconButton>

            <IconButton
              size="small"
              onClick={handleDeleteClick}
              disabled={!canDelete}
              color="error"
              title={
                canDelete
                  ? 'Eliminar movimiento'
                  : 'No se puede eliminar un movimiento conciliado'
              }
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </RowTableCell>
      </TableRow>

      <ConfirmationDialog
        open={isReconcileDialogOpen}
        onClose={handleCancelReconcile}
        onConfirm={handleConfirmReconcile}
        title={
          movement.is_reconciled
            ? 'Desconciliar movimiento bancario'
            : 'Conciliar movimiento bancario'
        }
        message={
          movement.is_reconciled
            ? '¿Está seguro que desea desconciliar el movimiento?'
            : '¿Está seguro que desea conciliar el movimiento?'
        }
        confirmText={movement.is_reconciled ? 'Desconciliar' : 'Conciliar'}
        confirmVariant={movement.is_reconciled ? 'danger' : 'base'}
        details={
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Fecha:</strong> {formatDate(movement.emission_date)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Tipo:</strong> {movement.movement_type_name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Concepto:</strong> {movement.concept || '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Comprobante:</strong> {movement.voucher_number || '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Beneficiario:</strong> {movement.legal_entity_name || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Importe:</strong> {formatCurrency(movement.amount, 2)}
            </Typography>
          </Box>
        }
        entityName="movimientos bancarios"
      />

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Eliminar movimiento bancario"
        message="¿Está seguro que desea eliminar el movimiento?"
        details={
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Fecha:</strong> {formatDate(movement.emission_date)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Tipo:</strong> {movement.movement_type_name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Concepto:</strong> {movement.concept || '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Comprobante:</strong> {movement.voucher_number || '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Beneficiario:</strong> {movement.legal_entity_name || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Importe:</strong> {formatCurrency(movement.amount, 2)}
            </Typography>
          </Box>
        }
        entityName="movimientos bancarios"
      />
    </>
  );
};

export default MovementRow;
